import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import './CyberBar.css';

/* ─── Utilities ─────────────────────────────────────────────────────── */
const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || navigator.vendor || window.opera
  );

/* ─── Text Decode Hook ───────────────────────────────────────────────── */
const DECODE_CHARS = 'アイウエ0123456789ABCDEF#$%&@';

function useDecodeText(target, active, charDelay = 45) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) { setDisplay(target); return; }

    let resolved = 0;
    let frame = 0;
    const len = target.length;

    function tick() {
      frame++;
      const framesPerChar = Math.max(1, Math.round(charDelay / 16));
      if (frame % framesPerChar === 0 && resolved < len) resolved++;

      let str = '';
      for (let i = 0; i < len; i++) {
        if (i < resolved || target[i] === ' ') {
          str += target[i];
        } else {
          str += DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)];
        }
      }
      setDisplay(str);
      if (resolved < len) rafRef.current = requestAnimationFrame(tick);
    }

    setDisplay(target.replace(/[^ ]/g, () => DECODE_CHARS[Math.floor(Math.random() * DECODE_CHARS.length)]));
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, target, charDelay]);

  return display;
}

/* ─── Decoded Link ───────────────────────────────────────────────────── */
function DecodedLink({ to, label, delay = 0, panelOpen, onClick, state }) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!panelOpen) { setActive(false); return; }
    const t = setTimeout(() => setActive(true), delay);
    return () => clearTimeout(t);
  }, [panelOpen, delay]);

  const text = useDecodeText(label, active, 38);

  return (
    <Link to={state ? { pathname: to, state } : to} className="panel-link" onClick={onClick}>
      <span className="panel-link-bracket">[</span>
      <span className="panel-link-text">{text}</span>
      <span className="panel-link-bracket">]</span>
    </Link>
  );
}

/* ─── Section Data ───────────────────────────────────────────────────── */
const SECTIONS = {
  hacking: {
    id: 'hacking',
    title: 'סייבר //',
    subtitle: 'CYBER DIVISION',
    links: [
      { to: '/cyber/hacking/guides', label: 'מדריכי סייבר' },
      { to: '/cyber/hacking/articles', label: 'מאמרי סייבר' },
      { to: '/cyber/hacking/videos', label: 'סרטוני סייבר' },
    ],
  },
  logo: {
    id: 'logo',
    title: 'מערכת ראשי //',
    subtitle: 'MAIN SYSTEM',
    links: [
      { to: '/cyber/works-with', label: 'שירות וידע' },
      { to: '/thanks', label: 'תודות', state: { from: 'cyber' } },
      { to: '/contact-us', label: 'צור קשר', state: { from: 'cyber' } },
    ],
  }
};

/* ─── Command Panel ──────────────────────────────────────────────────── */
function CommandPanel({ section, open, onMouseEnter, onMouseLeave }) {
  const data = SECTIONS[section];
  return (
    <div
      className={`command-panel ${open ? 'panel-open' : ''}`}
      aria-hidden={!open}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="panel-bg-grid" aria-hidden="true" />
      {data && (
        <div className="panel-inner">
          <div className="panel-header">
            <span className="panel-title">{data.title}</span>
            <span className="panel-subtitle">{data.subtitle}</span>
          </div>
          <div className="panel-links">
            {data.links.map((lk, i) => (
              <DecodedLink
                key={lk.to}
                to={lk.to}
                label={lk.label}
                delay={i * 90}
                panelOpen={open}
                onClick={onMouseLeave}
                state={lk.state}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── CyberBar ──────────────────────────────────────────────────────── */
function CyberBar() {
  const [activeSection, setActiveSection] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(null);
  const [mobileClosing, setMobileClosing] = useState(false);
  const [showBar, setShowBar] = useState(true);

  const timers = useRef({});
  const barRef = useRef(null);
  const touchStartY = useRef(0);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    if (mobile) {
      document.body.classList.add('mobile-device');
    } else {
      document.body.classList.remove('mobile-device');
    }
  }, []);

  const clear = () => Object.values(timers.current).forEach(clearTimeout);

  const openPanel = useCallback((section) => {
    clear();
    if (activeSection === section && panelOpen) return;
    if (panelOpen && activeSection !== section) {
      setActiveSection(section);
      return;
    }
    setActiveSection(section);
    timers.current.open = setTimeout(() => setPanelOpen(true), 0);
  }, [activeSection, panelOpen]);

  const closePanel = useCallback(() => {
    clear();
    timers.current.close = setTimeout(() => {
      setPanelOpen(false);
      timers.current.clear = setTimeout(() => setActiveSection(null), 320);
    }, 350);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(timers.current.close);
    clearTimeout(timers.current.clear);
  }, []);

  const handleScroll = useCallback(() => {
    const el = document.querySelector('.content.has-navbar');
    if (!el) return;
    const y = el.scrollTop;
    const up = y < lastScrollY.current || y < 50;
    if (showBar !== up) setShowBar(up);
    if (!up) { setPanelOpen(false); setActiveSection(null); }
    lastScrollY.current = y;
  }, [showBar]);

  const handleClickOutside = useCallback((e) => {
    if (!barRef.current) return;
    if (barRef.current.contains(e.target)) return;
    setPanelOpen(false);
    setActiveSection(null);
    clear();
  }, []);

  const handleTouchStart = (e) => (touchStartY.current = e.touches[0].clientY);
  const handleTouchMove = useCallback((e) => {
    if (!mobileMenu) return;
    if (e.touches[0].clientY - touchStartY.current > 70) closeMobileMenu();
  }, [mobileMenu]);

  const openMobileMenu = (section) => {
    if (mobileMenu === section) { closeMobileMenu(); return; }
    // #region agent log
    const sidebarEl = barRef.current;
    const sidebarTransform = sidebarEl ? getComputedStyle(sidebarEl).transform : 'no-sidebar';
    const sidebarRect = sidebarEl ? sidebarEl.getBoundingClientRect() : null;
    fetch('http://127.0.0.1:7242/ingest/e95aef97-19a2-4923-9f24-c950d27b618b',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'954b61'},body:JSON.stringify({sessionId:'954b61',location:'CyberBar.js:openMobileMenu',message:'openMobileMenu called',data:{section,isMobile,showBar,mobileMenu,mobileClosing,sidebarTransform,sidebarHeight:sidebarRect?.height,sidebarTop:sidebarRect?.top,viewportHeight:typeof window!=='undefined'?window.innerHeight:0},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    if (mobileMenu) {
      closeMobileMenu();
      setTimeout(() => setMobileMenu(section), 350);
    } else {
      setMobileMenu(section);
    }
  };

  const closeMobileMenu = () => {
    if (!mobileMenu) return;
    setMobileClosing(true);
    setTimeout(() => { setMobileMenu(null); setMobileClosing(false); }, 350);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    const el = document.querySelector('.content.has-navbar');
    if (el) el.addEventListener('scroll', handleScroll);
    else window.addEventListener('scroll', handleScroll);

    if (isMobile) {
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (el) el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      clear();
    };
  }, [isMobile, handleClickOutside, handleTouchMove, handleScroll]);

  useEffect(() => {
    if (!isMobile || !mobileMenu) return;
    const t = setTimeout(() => {
      const sheet = document.querySelector('.mobile-sheet');
      const overlay = document.querySelector('.mobile-overlay');
      const side = barRef.current;
      if (!sheet || !overlay) return;
      const sh = sheet.getBoundingClientRect();
      const ov = overlay.getBoundingClientRect();
      const sr = side?.getBoundingClientRect();
      fetch('http://127.0.0.1:7242/ingest/e95aef97-19a2-4923-9f24-c950d27b618b',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'954b61'},body:JSON.stringify({sessionId:'954b61',location:'CyberBar.js:useEffect(mobileMenu)',message:'after open',data:{sheetH:sh.height,sheetTop:sh.top,overlayH:ov.height,sidebarH:sr?.height,winH:window.innerHeight,winW:window.innerWidth,sheetCoversViewport:ov.height>=window.innerHeight*.9},timestamp:Date.now(),hypothesisId:'C'})}).catch(()=>{});
    }, 350);
    return () => clearTimeout(t);
  }, [isMobile, mobileMenu]);

  const renderIcon = (sectionKey, src, alt, to) => {
    const isActive = activeSection === sectionKey && panelOpen;
    return (
      <li
        key={sectionKey}
        className={`bar-icon ${isActive ? 'icon-active' : ''}`}
        onMouseEnter={() => !isMobile && openPanel(sectionKey)}
        onMouseLeave={() => !isMobile && closePanel()}
      >
        <Link
          to={to}
          className="menu-icon"
          onClick={(e) => {
            if (isMobile) { e.preventDefault(); openMobileMenu(sectionKey); }
          }}
          aria-expanded={isActive}
        >
          <span className="icon-ring" />
          <img src={src} alt={alt} />
          <span className="icon-glow" />
          <span className="fx-scan" aria-hidden="true" />
        </Link>
      </li>
    );
  };

  const items = useMemo(() => {
    const list = [];
    list.push(renderIcon('hacking', '/images/hacking.png', 'Cyber Hacking', '/cyber/hacking'));
    list.push(renderIcon('logo', '/images/logo.png', 'Logo', '/'));
    return list;
  }, [activeSection, panelOpen, isMobile, openPanel, closePanel]);

  const MobileSheet = () => {
    if (!isMobile || !mobileMenu) return null;
    const data = SECTIONS[mobileMenu];
    return (
      <>
        <div className="mobile-overlay" onClick={closeMobileMenu} />
        <div className={`mobile-sheet ${mobileClosing ? 'closing' : ''}`}
          onTouchStart={handleTouchStart} onTouchMove={handleTouchMove}>
          <div className="swipe-indicator" />
          <button className="close-sheet-btn" onClick={closeMobileMenu} aria-label="סגור">✕</button>
          <div className="sheet-header">
            <span className="sheet-title">{data?.title}</span>
            <span className="sheet-subtitle">{data?.subtitle}</span>
          </div>
          {[{ to: '/', label: 'חזור לדף הראשי' }, ...(data?.links || [])].map((lk, i) => (
            <Link key={lk.to + i} to={lk.state ? { pathname: lk.to, state: lk.state } : lk.to} className="sheet-link" onClick={closeMobileMenu}
              style={{ animationDelay: `${i * 80}ms` }}>
              <span className="sheet-link-bracket">[</span>
              {lk.label}
              <span className="sheet-link-bracket">]</span>
            </Link>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      <div ref={barRef} className={`sidebar ${showBar ? 'visible' : 'hidden'}`}>
        <div className="bar-grid" aria-hidden="true" />
        <div className="bar-fog" aria-hidden="true" />
        <div className="bar-horizon" aria-hidden="true" />
        <div className="bar-scanlines" aria-hidden="true" />

        <ul className="icon-strip">{items}</ul>

        {!isMobile && (
          <CommandPanel
            section={activeSection}
            open={panelOpen}
            onMouseEnter={cancelClose}
            onMouseLeave={closePanel}
          />
        )}
        {isMobile && mobileMenu && createPortal(
          <MobileSheet />,
          document.body
        )}
      </div>
    </>
  );
}

export default CyberBar;
